<!-- eslint-disable vue/max-len -->
<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import {
  IconArrowLeft,
  IconMoodSad,
} from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import { toast } from 'vue3-toastify';
import { useRoute } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { RefundOrigin } from '~/services/receipt-model-api/receipt-model-api.types';
import date from '~/utils/date';
import getErrorMessage from '~/utils/getErrorMessage';
import tag from '~/utils/tagsMapping';

import FinancingDocuments from '../blocks/FinancingDocuments.vue';
import FinancingStepsFlow from '../blocks/FinancingStepsFlow.vue';
import FinancingGeneralSection from '../blocks/steps/1.FinancingGeneralSection.vue';
import PartnerDetailsSection from '../blocks/steps/2.PartnerDetailsSection.vue';
import CancelProjectModal from '../components/CancelProjectModal.vue';
import TitularityTransferModal from '../components/TitularityTransferModal.vue';
import useFormalizationInfo from '../queries/contract-formalization/useFormalizationInfo';
import useCancelFormalized from '../queries/useCancelFormalized';
import useFinancingDetails from '../queries/useFinancingDetails';
import useProjectCancellation from '../queries/useProjectCancellation';
import { isMoreThanSevenBusinessDays } from '../utils/businessDays';
import { getTitularityTransferBlockReason } from '../utils/titularityTransfer';

const route = useRoute('/financing.[id]');

const id = computed(() => String(route.params.id ?? ''));
const loadingProject = ref(false);
const {
  data: financing,
  error: errorFinancing,
  isError: isErrorFinancing,
  isFetching: isFetchingFinancing,
  isLoading: isLoadingFinancing,
  refetch: refetchFinancing,
} = useFinancingDetails(id);

const { data: formalizationInfo } = useFormalizationInfo(id);

const {
  isPending: isCancelling,
  mutate: cancelProject,
} = useProjectCancellation();

const {
  isPending: isCancellingFormalized,
  mutate: cancelFormalized,
} = useCancelFormalized();

const showCancelModal = ref(false);
const showTitularityModal = ref(false);
const requiresForce = ref(false);

const isFormalized = computed(() => !!financing.value?.data.formalized_at);

const isCancelLoading = computed(() => (
  isFormalized.value ? isCancellingFormalized.value : isCancelling.value
));

const shouldShowSevenDaysWarning = computed(() => (
  isFormalized.value && isMoreThanSevenBusinessDays(financing.value?.data.formalized_at || '')
));

const handleTransferTitularity = () => {
  if (!financing.value) return;

  const blockReason = getTitularityTransferBlockReason(financing.value.data);
  if (blockReason) {
    toast.error(blockReason);
    return;
  }

  showTitularityModal.value = true;
};

const partnerId = computed(() => {
  const value = financing.value?.data.partner_id;
  if (value === null || value === undefined) return null;

  const parsed = typeof value === 'string' ? Number(value) : value;
  return Number.isNaN(parsed) ? null : parsed;
});

const handleCancelProject = (data: {
  cancelled_date?: string;
  force?: boolean;
  origin?: RefundOrigin;
  reason: string;
}) => {
  if (isFormalized.value) {
    cancelFormalized(
      {
        cancelledDate: data.cancelled_date,
        force: data.force,
        origin: data.origin,
        projectId: financing.value?.data.project_id!.toString() ?? '',
        reason: data.reason,
      },
      {
        onError: (error: any) => {
          const status = error?.response?.status;
          const detail = error?.response?.data?.detail;

          if (status === 400 && detail === 'Contract out of days to cancel') {
            requiresForce.value = true;
            return;
          }

          showCancelModal.value = false;
          requiresForce.value = false;
        },
        onSuccess: () => {
          loadingProject.value = true;
          showCancelModal.value = false;
          requiresForce.value = false;

          setTimeout(() => {
            refetchFinancing();
            loadingProject.value = false;
          }, 2000);
        },
      },
    );
  } else {
    cancelProject(
      {
        financingId: financing.value?.data.id!,
        reason: data.reason,
      },
      {
        onError: () => {
          toast.error('Erro ao cancelar projeto. Tente novamente.');
          showCancelModal.value = false;
        },
        onSuccess: () => {
          loadingProject.value = true;
          toast.success('Projeto cancelado com sucesso!');
          showCancelModal.value = false;

          setTimeout(() => {
            refetchFinancing();
            loadingProject.value = false;
          }, 2000);
        },
      },
    );
  }
};

function createSectionStatus(section: 'biometry' | 'contract') {
  const status = formalizationInfo.value?.[section]?.status;

  return {
    completedAt: computed(() => {
      const completed = financing.value?.data.section_statuses?.biometry_and_contract?.metadata?.[section]?.completed_at;
      if (!completed) return '(em andamento)';
      return date(completed).format('LLLL');
    }),
    lastSubmittedAt: computed(() => {
      const last = financing.value?.data.section_statuses?.biometry_and_contract?.metadata?.[section]?.last_submitted_at;
      if (!last) return '(não iniciado)';
      return date(last).format('LLLL');
    }),
    text: computed(() => {
      if (!status) return 'Aguardando';
      return tag[status]?.text || 'Aguardando';
    }),
    type: computed(() => {
      if (!status) return 'neutral';
      return tag[status]?.type || 'neutral';
    }),

  };
}

const biometrySection = computed(() => createSectionStatus('biometry'));
const contractSection = computed(() => createSectionStatus('contract'));
</script>

<template>
  <div class="financing-detail-bg">
    <DashboardLayout :loading="isLoadingFinancing || isFetchingFinancing">
      <div v-if="isLoadingFinancing || isCancelling || loadingProject">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="isErrorFinancing">
        <div class="empty">
          <div class="empty-icon">
            <IconMoodSad :size="64" :stroke-width="1.5" />
          </div>
          <p class="empty-title">Oops… Sem informações disponíveis</p>
          <p class="empty-subtitle text-secondary">
            Algo deu errado, tente novamente mais tarde.<br>
            Se você achar que isso é um erro, consulte o administrador da sua conta.
          </p>
          <p class="text-danger">Error: {{ getErrorMessage(errorFinancing) }}</p>
          <div class="empty-action">
            <a href="/financings" class="btn btn-primary">
              <IconArrowLeft :size="20" :stroke-width="2" class="me-1" />
              Ir para a listagem
            </a>
          </div>
        </div>
      </div>

      <div v-else-if="!financing">
        <p class="text-warning">No financing found.</p>
      </div>

      <div v-else class="row">
        <FinancingGeneralSection
          :financing="financing"
          :formalization-info="formalizationInfo"
          :is-cancel-loading="isCancelLoading"
          :loading-project="loadingProject"
          :project-id="id"
          @cancel-project="showCancelModal = true"
          @transfer-titularity="handleTransferTitularity"
        />

        <PartnerDetailsSection :partner-id="partnerId" />

        <div class="col-12">
          <ul class="nav nav-tabs mb-4" data-bs-toggle="tabs">
            <li class="nav-item">
              <a href="#tabs-financing-details" class="nav-link active" data-bs-toggle="tab">Etapas e status</a>
            </li>
            <li class="nav-item">
              <a href="#tabs-documents" class="nav-link" data-bs-toggle="tab">Documentos do contrato</a>
            </li>
          </ul>

          <div class="tab-content">
            <div class="tab-pane active show" id="tabs-financing-details">
              <FinancingStepsFlow
                :financing="financing"
                :formalization-info="formalizationInfo"
                :biometry-section="biometrySection"
                :contract-section="contractSection"
                @refresh="refetchFinancing"
              />
            </div>

            <div class="tab-pane" id="tabs-documents">
              <FinancingDocuments :financing-id="financing.data.id" />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>

    <div class="alert alert-warning mt-4 mx-3" role="alert">
      <div class="alert-with-icon">
        <i class="icon ti ti-alert-triangle" />
        <div class="content">
          <strong>Página em Construção</strong> - Esta página ainda está em desenvolvimento e pode não
          exibir todos os dados reais do financiamento.
        </div>
      </div>
    </div>

    <CancelProjectModal
      v-model="showCancelModal"
      :is-loading="isCancelLoading"
      :is-formalized="isFormalized"
      :formalization-date="financing?.data.formalized_at || undefined"
      :requires-force="requiresForce"
      :show-seven-days-warning="shouldShowSevenDaysWarning"
      @confirm="handleCancelProject" />

    <TitularityTransferModal
      v-if="financing"
      v-model="showTitularityModal"
      :current-document="financing.data.document"
      :current-name="financing.data.customer_name"
      :financing-project-id="financing.data.project_id"
      :project-id="id"
    />
  </div>
</template>

<style scoped lang="scss">
.alert-with-icon {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .icon {
    flex-shrink: 0;
    font-size: 1.5rem;
    opacity: 0.85;
  }

  .content {
    flex: 1;
  }
}

.financing-detail-bg {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e9ecef 100%);
  padding: 32px 0 0 0;
}
.financing-card {
  border-radius: 18px;
  box-shadow: 0 2px 12px 0 rgba(60, 72, 88, 0.10);
  border: none;
}

.status-informative {
  background: #e3f2fd;
  color: #1976d2;
}

.status-negative {
  background: #fdecea;
  color: #d32f2f;
}

.status-neutral {
  background: #f4f6f8;
  color: #616161;
}

.status-positive {
  background: #e8f5e9;
  color: #388e3c;
}

.status-warning {
  background: #fff8e1;
  color: #b26a00;
}

.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
